import { api } from "@glasshalf/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ConfirmImpactDialog } from "../../components/ConfirmImpactDialog";
import { StatusBadge } from "../../components/StatusBadge";
import { formatNumber } from "../../lib/format";
import { locations } from "../../lib/selectors";
import type { AnyRecord, AppData } from "../../lib/types";

type BarcodeOperation = "receive" | "move" | "issue";

type BarcodeWorkspaceProps = {
  data: AppData;
  title?: string;
  description?: string;
  initialOperation?: BarcodeOperation;
  initialFromLocationCode?: string;
};

export function BarcodePage({ data }: { data: AppData }) {
  const [params] = useSearchParams();
  return (
    <div className="page-stack barcode-page">
      <BarcodeWorkspace
        data={data}
        initialOperation={initialBarcodeOperation(params.get("operation"))}
        initialFromLocationCode={String(params.get("fromLocation") || "")}
      />
    </div>
  );
}

export function BarcodeWorkspace({
  data,
  title = "모바일 바코드 작업",
  description = "카메라 또는 직접 입력으로 SKU를 조회하고 입고, 이동, 출고 작업을 미리보기 후 기록합니다.",
  initialOperation = "receive",
  initialFromLocationCode = ""
}: BarcodeWorkspaceProps) {
  const queryClient = useQueryClient();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [scanMessage, setScanMessage] = useState("카메라 스캔을 시작하거나 바코드를 직접 입력하세요.");
  const [barcode, setBarcode] = useState("");
  const [operation, setOperation] = useState<BarcodeOperation>(initialOperation);
  const [quantity, setQuantity] = useState(1);
  const locationOptions = locations(data).filter((location) => location.active !== false);
  const [fromLocationCode, setFromLocationCode] = useState(initialFromLocationCode);
  const [toLocationCode, setToLocationCode] = useState(String(locationOptions[0]?.code || "WH_MAIN"));
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lookup = useMutation({ mutationFn: () => api.lookupBarcode(barcode.trim()) });
  const preview = useMutation({
    mutationFn: () => api.runBarcodeOperation(barcodePayload(true)),
    onSuccess: () => setConfirmOpen(true)
  });
  const runOperation = useMutation({
    mutationFn: () => api.runBarcodeOperation(barcodePayload(false)),
    onSuccess: async () => {
      setConfirmOpen(false);
      setReason("");
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    }
  });
  const item = (lookup.data?.item || preview.data?.item || runOperation.data?.item || null) as AnyRecord | null;

  function barcodePayload(dryRun: boolean) {
    return {
      barcode: barcode.trim(),
      operation,
      quantity,
      fromLocationCode: operation === "receive" ? undefined : fromLocationCode,
      toLocationCode: operation === "issue" ? undefined : toLocationCode,
      reason,
      dryRun
    };
  }

  async function startCameraScan() {
    const Detector = (window as AnyRecord).BarcodeDetector;
    if (!Detector) {
      setScanMessage("이 브라우저는 카메라 바코드 인식을 지원하지 않아 직접 입력으로 진행해 주세요.");
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
    if (videoRef.current) videoRef.current.srcObject = stream;
    const detector = new Detector({ formats: ["ean_13", "code_128", "qr_code"] });
    setScanMessage("바코드를 카메라 중앙에 맞춰 주세요.");
    const tick = async () => {
      if (!videoRef.current) return;
      const codes = await detector.detect(videoRef.current);
      const value = String(codes[0]?.rawValue || "");
      if (value) {
        setBarcode(value);
        setScanMessage(`스캔됨: ${value}`);
        stream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
        return;
      }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  useEffect(() => () => {
    const stream = videoRef.current?.srcObject as MediaStream | null;
    stream?.getTracks().forEach((track) => track.stop());
  }, []);

  return (
    <>
      <section className="toolbar-panel">
        <div className="archive-toolbar-copy">
          <strong>{title}</strong>
          <span>{description}</span>
        </div>
      </section>
      <section className="two-column barcode-workspace">
        <div className="panel barcode-camera-panel">
          <video ref={videoRef} autoPlay muted playsInline />
          <button type="button" onClick={startCameraScan}>
            <Camera size={15} />
            카메라 스캔
          </button>
          <p>{scanMessage}</p>
        </div>
        <div className="panel barcode-form-panel">
          <div className="form-grid">
            <label className="span-2">
              바코드
              <div className="inline-control">
                <input value={barcode} onChange={(event) => setBarcode(event.target.value)} placeholder="바코드, SKU, readable alias" />
                <button type="button" onClick={() => lookup.mutate()} disabled={!barcode.trim() || lookup.isPending}>
                  <Search size={15} />
                  조회
                </button>
              </div>
            </label>
            <label>
              작업
              <select value={operation} onChange={(event) => setOperation(event.target.value as BarcodeOperation)}>
                <option value="receive">입고</option>
                <option value="move">이동</option>
                <option value="issue">출고</option>
              </select>
            </label>
            <label>
              수량
              <input type="number" min={1} value={quantity} onChange={(event) => setQuantity(Math.max(1, Number(event.target.value || 1)))} />
            </label>
            {operation !== "receive" ? (
              <label>
                출발 위치
                <select value={fromLocationCode} onChange={(event) => setFromLocationCode(event.target.value)}>
                  <option value="">선택</option>
                  {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{String(location.name || location.code)}</option>)}
                </select>
              </label>
            ) : null}
            {operation !== "issue" ? (
              <label>
                도착 위치
                <select value={toLocationCode} onChange={(event) => setToLocationCode(event.target.value)}>
                  {locationOptions.map((location) => <option key={String(location.code)} value={String(location.code)}>{String(location.name || location.code)}</option>)}
                </select>
              </label>
            ) : null}
          </div>
          {lookup.error || preview.error || runOperation.error ? <p className="form-error">{errorMessage(lookup.error || preview.error || runOperation.error)}</p> : null}
          <button type="button" className="primary" disabled={!barcode.trim() || preview.isPending} onClick={() => preview.mutate()}>
            작업 미리보기
          </button>
        </div>
      </section>
      {item ? <BarcodeItem item={item} /> : null}
      <ConfirmImpactDialog
        open={confirmOpen}
        title="바코드 작업 확인"
        currentValue={item ? `${item.readableAlias || item.sku} · 현재 ${formatNumber(item.onHandTotal || 0)}개` : barcode}
        nextValue={`${operationLabel(operation)} ${formatNumber(quantity)}개`}
        externalImpact="온라인몰 수량은 직접 변경하지 않고 백오피스 실제 재고만 기록합니다."
        auditTrail="바코드, SKU, 위치, 수량, 작업 유형, 사유가 재고 원장과 감사 로그에 남습니다."
        reason={reason}
        confirmLabel="작업 기록"
        busy={runOperation.isPending}
        onReasonChange={setReason}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => runOperation.mutate()}
      />
    </>
  );
}

function BarcodeItem({ item }: { item: AnyRecord }) {
  return (
    <section className="panel barcode-result-panel">
      <div className="panel-head">
        <div>
          <h2>{item.productName || item.readableAlias || item.sku}</h2>
          <p>{item.readableAlias || item.sku} · {item.barcode || item.gtin || "바코드 미확인"}</p>
        </div>
        <StatusBadge tone={Number(item.availableToSell || 0) > 0 ? "success" : "warning"}>가용 {formatNumber(item.availableToSell || 0)}</StatusBadge>
      </div>
      <div className="sync-list">
        {(item.balances || []).map((balance: AnyRecord) => (
          <div className="sync-row" key={String(balance.locationCode)}>
            <div>
              <strong>{balance.locationName || balance.locationCode}</strong>
              <span>{balance.sellableState === "sellable" ? "판매 가능" : "비판매"}</span>
            </div>
            <strong>{formatNumber(balance.quantity || 0)}개</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function operationLabel(operation: BarcodeOperation) {
  if (operation === "receive") return "입고";
  if (operation === "move") return "이동";
  return "출고";
}

function initialBarcodeOperation(value: string | null): BarcodeOperation {
  if (value === "move" || value === "issue" || value === "receive") return value;
  return "receive";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "요청 처리에 실패했습니다.";
}
